import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { formatCurrency as fmtCur, getCurrencySymbol } from '@/lib/market';
import {
  Home, Plus, Edit2, Trash2, MapPin, TrendingUp, TrendingDown,
  Building2, DollarSign, Percent, Calendar, X, Save,
  Users, Key, Landmark
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';



const emptyProperty = {
  name: '',
  property_type: 'residential',
  status: 'owned',
  description: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  country: 'India',
  postal_code: '',
  area_sqft: '',
  bedrooms: '',
  bathrooms: '',
  parking_spaces: '',
  year_built: '',
  purchase_date: '',
  purchase_price: '',
  current_value: '',
  is_rented: false,
  monthly_rent: '',
  tenant_name: '',
  lease_start_date: '',
  lease_end_date: '',
  has_loan: false,
  loan_amount: '',
  loan_outstanding: '',
  loan_emi: '',
  loan_interest_rate: '',
  loan_end_date: '',
  notes: '',
};

export default function RealEstateManagementPage() {
  const { user, signOut } = useAuth();
  const { propertyTypes, propertyStatuses } = useAppData();
  const formatCurrency = (value) => fmtCur(value, user?.market);
  const currSym = getCurrencySymbol(user?.market);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProperty, setCurrentProperty] = useState(emptyProperty);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [propertiesRes, portfolioRes] = await Promise.all([
        axios.get(`${API}/real-estate/properties`),
        axios.get(`${API}/real-estate/portfolio`)
      ]);
      setProperties(propertiesRes.data || []);
      setPortfolio(portfolioRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddNew = () => {
    setCurrentProperty(emptyProperty);
    setIsEditing(false);
    setIsDialogOpen(true);
  };

  const handleEdit = (property) => {
    setCurrentProperty({
      ...emptyProperty,
      ...property,
      area_sqft: property.area_sqft || '',
      bedrooms: property.bedrooms || '',
      bathrooms: property.bathrooms || '',
      parking_spaces: property.parking_spaces || '',
      year_built: property.year_built || '',
      purchase_price: property.purchase_price || '',
      current_value: property.current_value || '',
      monthly_rent: property.monthly_rent || '',
      loan_amount: property.loan_amount || '',
      loan_outstanding: property.loan_outstanding || '',
      loan_emi: property.loan_emi || '',
      loan_interest_rate: property.loan_interest_rate || '',
    });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const handleDelete = async (propertyId) => {
    try {
      await axios.delete(`${API}/real-estate/properties/${propertyId}`);
      toast.success('Property deleted successfully');
      setDeleteConfirmId(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Failed to delete property');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare data
      const data = {
        ...currentProperty,
        user_id: user.id,
        area_sqft: currentProperty.area_sqft ? parseFloat(currentProperty.area_sqft) : null,
        bedrooms: currentProperty.bedrooms ? parseInt(currentProperty.bedrooms) : null,
        bathrooms: currentProperty.bathrooms ? parseInt(currentProperty.bathrooms) : null,
        parking_spaces: currentProperty.parking_spaces ? parseInt(currentProperty.parking_spaces) : null,
        year_built: currentProperty.year_built ? parseInt(currentProperty.year_built) : null,
        purchase_price: currentProperty.purchase_price ? parseFloat(currentProperty.purchase_price) : null,
        current_value: currentProperty.current_value ? parseFloat(currentProperty.current_value) : null,
        monthly_rent: currentProperty.monthly_rent ? parseFloat(currentProperty.monthly_rent) : null,
        loan_amount: currentProperty.loan_amount ? parseFloat(currentProperty.loan_amount) : null,
        loan_outstanding: currentProperty.loan_outstanding ? parseFloat(currentProperty.loan_outstanding) : null,
        loan_emi: currentProperty.loan_emi ? parseFloat(currentProperty.loan_emi) : null,
        loan_interest_rate: currentProperty.loan_interest_rate ? parseFloat(currentProperty.loan_interest_rate) : null,
      };

      if (isEditing) {
        await axios.put(`${API}/real-estate/properties/${currentProperty.id}`, data);
        toast.success('Property updated successfully');
      } else {
        await axios.post(`${API}/real-estate/properties`, data);
        toast.success('Property added successfully');
      }

      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving property:', error);
      toast.error(error.response?.data?.detail || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setCurrentProperty(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="real-estate-management">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-sm">
            <Home className="w-5 h-5 text-[#1a1a1a]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1a1a1a]">Real Estate</h1>
            <p className="text-sm text-[#4a4a4a]">Manage your properties</p>
          </div>
        </div>
        <Button
          onClick={handleAddNew}
          className="bg-[#ffd87a] hover:bg-[#f5cc6a] text-black"
          data-testid="add-property-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Property
        </Button>
      </div>

      <div className="space-y-6">
        {/* Portfolio Summary */}
        {portfolio && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="rounded-2xl bg-gradient-to-br from-[#043b65] to-[#0a5a8a] p-4 shadow-md">
              <p className="text-xs text-[#ffd87a] uppercase tracking-widest mb-1">Total Value</p>
              <p className="text-xl font-bold text-white">{formatCurrency(portfolio.total_value)}</p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-[#8ba888] to-[#6b7c5c] p-4 shadow-md">
              <p className="text-xs text-white/80 uppercase tracking-widest mb-1">Total Equity</p>
              <p className="text-xl font-bold text-white">{formatCurrency(portfolio.total_equity)}</p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-[#0a5a8a] to-[#1e5f99] p-4 shadow-md">
              <p className="text-xs text-[#ffd87a] uppercase tracking-widest mb-1">Annual Rent</p>
              <p className="text-xl font-bold text-white">{formatCurrency(portfolio.total_annual_rent)}</p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-amber-600 to-yellow-500 p-4 shadow-md">
              <p className="text-xs text-amber-100 uppercase tracking-widest mb-1">Rental Yield</p>
              <p className="text-xl font-bold text-white">{(portfolio.portfolio_yield || 0).toFixed(2)}%</p>
            </div>
            <div className={`rounded-2xl p-4 shadow-md ${(portfolio.total_gain || 0) >= 0 ? 'bg-gradient-to-br from-[#1e5f99] to-[#2c7bb8]' : 'bg-gradient-to-br from-red-700 to-red-500'}`}>
              <p className="text-xs text-[#ffd87a] uppercase tracking-widest mb-1">Total Gain</p>
              <p className="text-xl font-bold text-white">{formatCurrency(portfolio.total_gain)}</p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-[#05bebe] to-[#0a5a8a] p-4 shadow-md">
              <p className="text-xs text-white/70 uppercase tracking-widest mb-1">Properties</p>
              <p className="text-xl font-bold text-white">{portfolio.properties_count}</p>
            </div>
          </div>
        )}

        {/* Properties List */}
        <div className="rounded-2xl overflow-hidden border border-[#1e5f99]/20 shadow-md">
          {properties.length === 0 ? (
            <div className="text-center py-16 bg-white">
              <Home className="w-12 h-12 text-[#043b65] mx-auto mb-4" />
              <p className="text-[#4a4a4a] mb-4">No properties added yet</p>
              <Button onClick={handleAddNew} className="bg-[#ffd87a] hover:bg-[#f5cc6a] text-black">
                <Plus className="w-4 h-4 mr-2" /> Add Your First Property
              </Button>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#043b65] to-[#0a5a8a]">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">Property</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">Location</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">Type</th>
                  <th className="text-right px-4 py-3.5 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">Current Value</th>
                  <th className="text-right px-4 py-3.5 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">Gain</th>
                  <th className="text-right px-4 py-3.5 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">Monthly Rent</th>
                  <th className="text-center px-4 py-3.5 text-xs font-semibold text-[#ffd87a] uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1e5f99]/10">
                {properties.map((property, idx) => (
                  <tr
                    key={property.id}
                    className={`transition-colors hover:bg-[#05bebe]/5 ${idx % 2 === 0 ? 'bg-white' : 'bg-[#043b65]/[0.02]'}`}
                    data-testid={`property-${property.id}`}
                  >
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-[#ffd87a]/10 flex items-center justify-center shrink-0">
                          <Home className="w-4 h-4 text-[#ffd87a]" />
                        </div>
                        <div>
                          <p className="font-semibold text-[#043b65]">{property.name}</p>
                          <p className="text-xs text-[#4a4a4a]">{property.area_sqft ? `${property.area_sqft.toLocaleString()} sq.ft` : '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1 text-sm text-[#4a4a4a]">
                        <MapPin className="w-3 h-3 text-[#4a4a4a] shrink-0" />
                        {property.city}, {property.state}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 capitalize">
                        {property.property_type}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right font-semibold text-[#043b65]">
                      {formatCurrency(property.current_value)}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className={`flex items-center justify-end gap-1 font-semibold text-sm ${(property.gain_percent || 0) >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                        {(property.gain_percent || 0) >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        {(property.gain_percent || 0).toFixed(1)}%
                      </div>
                      <p className="text-xs text-[#4a4a4a] text-right">{formatCurrency(property.gain)}</p>
                    </td>
                    <td className="px-4 py-4 text-right text-sm font-medium text-[#2a2a2a]">
                      {property.is_rented ? formatCurrency(property.monthly_rent) : <span className="text-[#4a4a4a]">—</span>}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${property.is_rented ? 'bg-[#d9e2c3] text-[#1a1a1a]' : 'bg-[#f5f5f5] text-[#4a4a4a]'}`}>
                        {property.is_rented ? 'Rented' : 'Owned'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(property)} className="text-[#4a4a4a] hover:text-[#1a1a1a]" data-testid={`edit-${property.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(property.id)} className="text-[#4a4a4a] hover:text-red-500" data-testid={`delete-${property.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add/Edit Property Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white border-[#e5e5e5]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a1a]">
              {isEditing ? 'Edit Property' : 'Add New Property'}
            </DialogTitle>
            <DialogDescription>
              Fill in the property details below
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="bg-[#f5f5f5] border border-[#e5e5e5] mb-4">
              <TabsTrigger value="basic"      className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">Basic Info</TabsTrigger>
              <TabsTrigger value="financial"  className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">Financial</TabsTrigger>
              <TabsTrigger value="rental"     className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">Rental</TabsTrigger>
              <TabsTrigger value="loan"       className="data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black">Loan</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="name">Property Name *</Label>
                  <Input
                    id="name"
                    value={currentProperty.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="e.g., Mumbai Apartment"
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="property_type">Property Type</Label>
                  <Select value={currentProperty.property_type} onValueChange={(v) => updateField('property_type', v)}>
                    <SelectTrigger className="bg-white border-[#e5e5e5]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypes.map(t => (
                        <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={currentProperty.status} onValueChange={(v) => updateField('status', v)}>
                    <SelectTrigger className="bg-white border-[#e5e5e5]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyStatuses.map(s => (
                        <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="address_line1">Address Line 1 *</Label>
                  <Input
                    id="address_line1"
                    value={currentProperty.address_line1}
                    onChange={(e) => updateField('address_line1', e.target.value)}
                    placeholder="Street address"
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="address_line2">Address Line 2</Label>
                  <Input
                    id="address_line2"
                    value={currentProperty.address_line2}
                    onChange={(e) => updateField('address_line2', e.target.value)}
                    placeholder="Apartment, floor, etc."
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={currentProperty.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    placeholder="City"
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={currentProperty.state}
                    onChange={(e) => updateField('state', e.target.value)}
                    placeholder="State"
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="area_sqft">Area (sq.ft)</Label>
                  <Input
                    id="area_sqft"
                    type="number"
                    value={currentProperty.area_sqft}
                    onChange={(e) => updateField('area_sqft', e.target.value)}
                    placeholder="2500"
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="year_built">Year Built</Label>
                  <Input
                    id="year_built"
                    type="number"
                    value={currentProperty.year_built}
                    onChange={(e) => updateField('year_built', e.target.value)}
                    placeholder="2020"
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="bedrooms">Bedrooms</Label>
                  <Input
                    id="bedrooms"
                    type="number"
                    value={currentProperty.bedrooms}
                    onChange={(e) => updateField('bedrooms', e.target.value)}
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="bathrooms">Bathrooms</Label>
                  <Input
                    id="bathrooms"
                    type="number"
                    value={currentProperty.bathrooms}
                    onChange={(e) => updateField('bathrooms', e.target.value)}
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="financial" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="purchase_date">Purchase Date</Label>
                  <Input
                    id="purchase_date"
                    type="date"
                    value={currentProperty.purchase_date}
                    onChange={(e) => updateField('purchase_date', e.target.value)}
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="purchase_price">Purchase Price ({currSym})</Label>
                  <Input
                    id="purchase_price"
                    type="number"
                    value={currentProperty.purchase_price}
                    onChange={(e) => updateField('purchase_price', e.target.value)}
                    placeholder="50000000"
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
                <div>
                  <Label htmlFor="current_value">Current Value ({currSym})</Label>
                  <Input
                    id="current_value"
                    type="number"
                    value={currentProperty.current_value}
                    onChange={(e) => updateField('current_value', e.target.value)}
                    placeholder="70000000"
                    className="bg-white border-[#e5e5e5]"
                  />
                </div>
              </div>
              <div className="col-span-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={currentProperty.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  placeholder="Additional notes about the property"
                  className="bg-white border-[#e5e5e5]"
                  rows={3}
                />
              </div>
            </TabsContent>

            <TabsContent value="rental" className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-[#f9f9f9]">
                <Switch
                  checked={currentProperty.is_rented}
                  onCheckedChange={(v) => updateField('is_rented', v)}
                />
                <Label>Property is rented out</Label>
              </div>
              
              {currentProperty.is_rented && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="monthly_rent">Monthly Rent ({currSym})</Label>
                    <Input
                      id="monthly_rent"
                      type="number"
                      value={currentProperty.monthly_rent}
                      onChange={(e) => updateField('monthly_rent', e.target.value)}
                      placeholder="150000"
                      className="bg-white border-[#e5e5e5]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tenant_name">Tenant Name</Label>
                    <Input
                      id="tenant_name"
                      value={currentProperty.tenant_name}
                      onChange={(e) => updateField('tenant_name', e.target.value)}
                      placeholder="Tenant or company name"
                      className="bg-white border-[#e5e5e5]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lease_start_date">Lease Start Date</Label>
                    <Input
                      id="lease_start_date"
                      type="date"
                      value={currentProperty.lease_start_date}
                      onChange={(e) => updateField('lease_start_date', e.target.value)}
                      className="bg-white border-[#e5e5e5]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lease_end_date">Lease End Date</Label>
                    <Input
                      id="lease_end_date"
                      type="date"
                      value={currentProperty.lease_end_date}
                      onChange={(e) => updateField('lease_end_date', e.target.value)}
                      className="bg-white border-[#e5e5e5]"
                    />
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="loan" className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-[#f9f9f9]">
                <Switch
                  checked={currentProperty.has_loan}
                  onCheckedChange={(v) => updateField('has_loan', v)}
                />
                <Label>Property has a loan/mortgage</Label>
              </div>

              {currentProperty.has_loan && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="loan_amount">Loan Amount ({currSym})</Label>
                    <Input
                      id="loan_amount"
                      type="number"
                      value={currentProperty.loan_amount}
                      onChange={(e) => updateField('loan_amount', e.target.value)}
                      placeholder="35000000"
                      className="bg-white border-[#e5e5e5]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="loan_outstanding">Outstanding ({currSym})</Label>
                    <Input
                      id="loan_outstanding"
                      type="number"
                      value={currentProperty.loan_outstanding}
                      onChange={(e) => updateField('loan_outstanding', e.target.value)}
                      placeholder="28000000"
                      className="bg-white border-[#e5e5e5]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="loan_emi">Monthly EMI ({currSym})</Label>
                    <Input
                      id="loan_emi"
                      type="number"
                      value={currentProperty.loan_emi}
                      onChange={(e) => updateField('loan_emi', e.target.value)}
                      placeholder="320000"
                      className="bg-white border-[#e5e5e5]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="loan_interest_rate">Interest Rate (%)</Label>
                    <Input
                      id="loan_interest_rate"
                      type="number"
                      step="0.1"
                      value={currentProperty.loan_interest_rate}
                      onChange={(e) => updateField('loan_interest_rate', e.target.value)}
                      placeholder="8.5"
                      className="bg-white border-[#e5e5e5]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="loan_end_date">Loan End Date</Label>
                    <Input
                      id="loan_end_date"
                      type="date"
                      value={currentProperty.loan_end_date}
                      onChange={(e) => updateField('loan_end_date', e.target.value)}
                      className="bg-white border-[#e5e5e5]"
                    />
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#e5e5e5]">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#ffd87a] hover:bg-[#f5cc6a] text-black">
              {saving ? 'Saving...' : (isEditing ? 'Update Property' : 'Add Property')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent className="bg-white border-[#e5e5e5]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1a1a]">Delete Property</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this property? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="border-[#e5e5e5]">
              Cancel
            </Button>
            <Button onClick={() => handleDelete(deleteConfirmId)} className="bg-red-600 hover:bg-red-700">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
